import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  NotebookPen,
  Trash2,
  Loader2,
  Smile,
  Mic,
  Square as SquareIcon,
  Play,
  Pause,
  X as XIcon,
} from "lucide-react";
import { plants } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import DatePickerButton from "@/components/result/DatePickerButton";

// Default fallback set — used only if the /api/plants/journal/emojis
// endpoint fails. Server is authoritative and silently drops unknown values,
// so a stale client list can never break the save path.
const FALLBACK_EMOJIS = ["🌸", "🌱", "🌿", "🍅", "💧", "☀️", "❄️", "✂️", "🪴", "🐛", "🌰", "📝"];

/**
 * Compact `<audio>` player for a saved journal voice note.
 *
 * Fetches the audio data URL lazily on first play — the list-parent
 * doesn't preload dozens of memos when the timeline mounts. Toggles a
 * play/pause button with an elapsed / total readout.
 */
const VoicePlayback = ({ scanId, entryId, durationMs, onDelete }) => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const audioRef = useRef(null);

  const totalS = Math.max(1, Math.round((durationMs || 0) / 1000));
  const load = async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      const { data } = await plants.getVoiceNote(scanId, entryId);
      const el = audioRef.current;
      if (el) {
        el.src = data.audio_data_url;
        setLoaded(true);
      }
    } catch {
      toast.error(t("journal.voice.loadFail"));
    } finally {
      setLoading(false);
    }
  };
  const toggle = async () => {
    if (!loaded) await load();
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      try { await el.play(); } catch { /* browser autoplay policy */ }
    }
  };
  const remove = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await plants.deleteVoiceNote(scanId, entryId);
      onDelete?.();
    } catch {
      toast.error(t("journal.voice.deleteFail"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="mt-2 inline-flex items-center gap-2 rounded-full bg-parchment/80 border border-[#EFEBE3] pl-2 pr-1 py-0.5"
      data-testid={`journal-voice-${entryId}`}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? t("journal.voice.pause") : t("journal.voice.play")}
        data-testid={`journal-voice-toggle-${entryId}`}
        className="w-6 h-6 rounded-full bg-leaf hover:bg-leaf-deep text-white flex items-center justify-center transition"
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
        ) : playing ? (
          <Pause className="w-3 h-3" strokeWidth={1.5} />
        ) : (
          <Play className="w-3 h-3 translate-x-[1px]" strokeWidth={1.5} />
        )}
      </button>
      <span className="text-[11px] text-leaf-mist [font-variant-numeric:lining-nums_tabular-nums]">
        {totalS}s
      </span>
      <button
        type="button"
        onClick={remove}
        disabled={deleting}
        aria-label={t("journal.voice.remove")}
        data-testid={`journal-voice-delete-${entryId}`}
        className="w-6 h-6 rounded-full text-leaf-mist hover:text-rust flex items-center justify-center transition disabled:opacity-50"
      >
        <XIcon className="w-3 h-3" strokeWidth={1.5} />
      </button>
      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
};

/**
 * Result-page plant-care journal.
 *
 * Users add short dated notes to the plant, optionally prefixed with an
 * emoji from a fixed whitelist and/or a short voice memo. Everything
 * persists via `/api/plants/{scan_id}/journal[/voice]`; delete cascades
 * the voice note.
 *
 * Voice notes are auth-only — the whole card only renders on private
 * `/plant/{scan_id}` routes, and we further gate the mic button on
 * `useAuth().user` for defence-in-depth.
 */
export const JournalCard = ({ scanId, journal = [], onAdd, onDelete, onUpdate }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);
  const [text, setText] = useState("");
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [emojiOptions, setEmojiOptions] = useState(FALLBACK_EMOJIS);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);

  // Fetch the server-authoritative whitelist once — future set widenings
  // land without a client rebuild.
  useEffect(() => {
    let cancelled = false;
    plants.listJournalEmojis()
      .then(({ data }) => { if (!cancelled && Array.isArray(data?.emojis) && data.emojis.length) setEmojiOptions(data.emojis); })
      .catch(() => { /* fallback list is fine */ });
    return () => { cancelled = true; };
  }, []);

  const voice = useVoiceRecorder();
  // We record + upload the audio in TWO stages:
  //   1. `submit()` creates the text entry → returns entry_id
  //   2. If we captured a voice memo AND it's within the size cap, POST
  //      it against that entry_id via `attachVoiceNote`.
  // The recorded blob is stashed in `pendingVoiceRef` between record-stop
  // and text-submit so a user can hit "record → speak → done → type
  // caption → send" in the natural order.
  const pendingVoiceRef = useRef(null);
  const [hasPendingVoice, setHasPendingVoice] = useState(false);
  const [pendingVoiceMs, setPendingVoiceMs] = useState(0);

  const trimmed = text.trim();
  const canSubmit = (trimmed.length > 0 || hasPendingVoice) && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      // Text entry must exist first so the voice-note endpoint has an
      // entry_id to attach to. When the user recorded a voice memo but
      // typed nothing, we fall back to a whitespace placeholder that the
      // server treats as the label "Voice note".
      const entryText = trimmed.length ? trimmed : t("journal.voice.entryFallback");
      const { data } = await plants.addJournalEntry(scanId, {
        text: entryText,
        entry_date: entryDate,
        emoji: selectedEmoji || undefined,
      });
      let finalEntry = data;
      if (hasPendingVoice && pendingVoiceRef.current) {
        try {
          const { data: v } = await plants.attachVoiceNote(scanId, data.entry_id, {
            audio_data_url: pendingVoiceRef.current.dataUrl,
            duration_ms: pendingVoiceRef.current.durationMs,
          });
          finalEntry = { ...data, voice_note_id: v.voice_note_id };
        } catch (e) {
          // Text saved but voice attach failed — warn but don't block.
          const msg = e?.response?.data?.detail || t("journal.voice.attachFail");
          toast.error(String(msg));
        }
      }
      onAdd?.(finalEntry);
      setText("");
      setSelectedEmoji(null);
      pendingVoiceRef.current = null;
      setHasPendingVoice(false);
      setPendingVoiceMs(0);
    } catch (e) {
      const msg = e?.response?.data?.detail || t("journal.addFail");
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry_id) => {
    if (deletingId) return;
    setDeletingId(entry_id);
    try {
      await plants.deleteJournalEntry(scanId, entry_id);
      onDelete?.(entry_id);
    } catch {
      toast.error(t("journal.deleteFail"));
    } finally {
      setDeletingId(null);
    }
  };

  const toggleRecord = async () => {
    if (voice.isRecording) {
      const out = await voice.stop();
      if (out) {
        pendingVoiceRef.current = { dataUrl: out.dataUrl, durationMs: out.durationMs };
        setHasPendingVoice(true);
        setPendingVoiceMs(out.durationMs);
      }
      return;
    }
    if (!isAuthenticated) {
      toast.message(t("journal.voice.signInRequired"));
      return;
    }
    try {
      await voice.start();
    } catch {
      toast.error(t("journal.voice.micDenied"));
    }
  };

  const discardPendingVoice = () => {
    pendingVoiceRef.current = null;
    setHasPendingVoice(false);
    setPendingVoiceMs(0);
  };

  const patchEntryLocally = (entry_id, patch) => {
    onUpdate?.(entry_id, patch);
  };

  const sorted = useMemo(() => {
    return [...(journal || [])].sort((a, b) => {
      const d = String(b.entry_date || "").localeCompare(String(a.entry_date || ""));
      if (d !== 0) return d;
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
  }, [journal]);

  const locale = i18n.language === "es" ? "es-ES" : "en-US";
  const formatDate = (isoDay) => {
    if (!isoDay) return "";
    const d = new Date(isoDay + "T12:00:00Z");
    if (Number.isNaN(d.getTime())) return isoDay;
    return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
  };

  const recordingSecs = Math.floor(voice.elapsedMs / 1000);
  const recordingMaxS = Math.round(voice.maxMs / 1000);

  return (
    <section
      data-testid="journal-card"
      className="rounded-2xl bg-white border border-[#EFEBE3] p-4 md:p-5 shadow-botanical"
    >
      <div className="flex items-center gap-2 text-leaf-mist text-[11px] uppercase tracking-[0.14em]">
        <NotebookPen className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
        {t("journal.title")}
      </div>

      {/* Add row */}
      <div className="mt-3 space-y-2">
        <label htmlFor={`journal-text-${scanId}`} className="sr-only">
          {t("journal.placeholder")}
        </label>
        <textarea
          id={`journal-text-${scanId}`}
          data-testid="journal-input"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 280))}
          placeholder={t("journal.placeholder")}
          rows={2}
          maxLength={280}
          disabled={busy}
          className="w-full rounded-xl border border-[#E8E5DF] bg-parchment/40 px-3 py-2 text-sm text-leaf-ink placeholder:text-leaf-mist/70 focus:outline-none focus:ring-2 focus:ring-leaf/50 resize-none"
        />

        <div className="flex items-center gap-2 justify-between flex-wrap">
          <div className="flex items-center gap-1.5">
            <DatePickerButton
              value={entryDate}
              onChange={setEntryDate}
              max={new Date().toISOString().slice(0, 10)}
              disabled={busy}
              testId="journal-date"
              ariaLabel={t("journal.dateLabel")}
              size="xs"
            />

            {/* Emoji picker */}
            <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="journal-emoji-picker-trigger"
                  disabled={busy}
                  aria-label={t("journal.emoji.pick")}
                  className="w-8 h-8 rounded-full border border-[#E8E5DF] bg-white flex items-center justify-center hover:border-leaf/40 transition disabled:opacity-50"
                >
                  {selectedEmoji ? (
                    <span className="text-base leading-none" data-testid="journal-emoji-current">{selectedEmoji}</span>
                  ) : (
                    <Smile className="w-4 h-4 text-leaf-mist" strokeWidth={1.5} />
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-2" data-testid="journal-emoji-picker">
                <div className="grid grid-cols-6 gap-1">
                  {emojiOptions.map((e) => (
                    <button
                      key={e}
                      type="button"
                      data-testid={`journal-emoji-option-${e}`}
                      onClick={() => { setSelectedEmoji(e); setEmojiOpen(false); }}
                      className={`w-8 h-8 rounded-md flex items-center justify-center text-base hover:bg-parchment transition ${selectedEmoji === e ? "bg-parchment" : ""}`}
                    >
                      {e}
                    </button>
                  ))}
                  {selectedEmoji && (
                    <button
                      type="button"
                      data-testid="journal-emoji-clear"
                      onClick={() => { setSelectedEmoji(null); setEmojiOpen(false); }}
                      className="col-span-6 mt-1 pt-1 border-t border-[#EFEBE3] text-xs text-leaf-mist hover:text-leaf py-1"
                    >
                      {t("journal.emoji.clear")}
                    </button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Voice recorder — auth-only. Rendered greyed with a
                sign-in-required toast when not signed in so it isn't a
                mystery-missing button. */}
            {voice.isSupported && (
              <button
                type="button"
                data-testid="journal-voice-record"
                onClick={toggleRecord}
                disabled={busy}
                aria-label={
                  voice.isRecording
                    ? t("journal.voice.stopAria")
                    : t("journal.voice.recordAria")
                }
                aria-pressed={voice.isRecording}
                className={`w-8 h-8 rounded-full flex items-center justify-center border transition disabled:opacity-50 ${
                  voice.isRecording
                    ? "border-rust bg-rust/10 text-rust animate-pulse"
                    : "border-[#E8E5DF] bg-white text-leaf-mist hover:border-leaf/40"
                }`}
              >
                {voice.isRecording ? (
                  <SquareIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                ) : (
                  <Mic className="w-4 h-4" strokeWidth={1.5} />
                )}
              </button>
            )}

            {voice.isRecording && (
              <span
                className="text-xs text-rust [font-variant-numeric:lining-nums_tabular-nums]"
                data-testid="journal-voice-timer"
              >
                {recordingSecs}s / {recordingMaxS}s
              </span>
            )}

            {hasPendingVoice && !voice.isRecording && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-parchment px-2 py-0.5 text-[11px] text-leaf-ink"
                data-testid="journal-voice-pending"
              >
                <Mic className="w-3 h-3 text-leaf" strokeWidth={1.5} />
                {Math.max(1, Math.round(pendingVoiceMs / 1000))}s
                <button
                  type="button"
                  onClick={discardPendingVoice}
                  aria-label={t("journal.voice.discard")}
                  data-testid="journal-voice-pending-discard"
                  className="text-leaf-mist hover:text-rust ml-0.5"
                >
                  <XIcon className="w-3 h-3" strokeWidth={1.5} />
                </button>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] text-leaf-mist [font-variant-numeric:lining-nums_tabular-nums]">
              {trimmed.length}/280
            </span>
            <Button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="journal-add"
              size="sm"
              className="rounded-full bg-leaf hover:bg-leaf-deep text-white disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
              ) : (
                t("journal.add")
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {sorted.length === 0 ? (
        <p className="mt-4 text-xs text-leaf-mist" data-testid="journal-empty">
          {t("journal.emptyHint")}
        </p>
      ) : (
        <ol className="mt-4 space-y-3 border-t border-[#EFEBE3] pt-3" data-testid="journal-timeline">
          {sorted.map((entry) => (
            <li
              key={entry.entry_id}
              data-testid={`journal-entry-${entry.entry_id}`}
              className="flex items-start gap-3"
            >
              <div className="mt-0.5 flex flex-col items-center pt-0.5">
                <span
                  className="w-2 h-2 rounded-full bg-leaf shrink-0"
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] text-leaf-mist uppercase tracking-wider">
                    {formatDate(entry.entry_date)}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(entry.entry_id)}
                    disabled={deletingId === entry.entry_id}
                    data-testid={`journal-delete-${entry.entry_id}`}
                    aria-label={t("journal.deleteAria")}
                    className="text-leaf-mist hover:text-rust transition disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                  </button>
                </div>
                <p className="text-sm text-leaf-ink whitespace-pre-wrap break-words leading-snug">
                  {entry.emoji && (
                    <span
                      className="mr-1"
                      data-testid={`journal-entry-emoji-${entry.entry_id}`}
                      aria-hidden="true"
                    >
                      {entry.emoji}
                    </span>
                  )}
                  {entry.text}
                </p>
                {entry.voice_note_id && (
                  <VoicePlayback
                    scanId={scanId}
                    entryId={entry.entry_id}
                    durationMs={0}
                    onDelete={() =>
                      patchEntryLocally(entry.entry_id, { voice_note_id: null })
                    }
                  />
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default JournalCard;
